import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductsPage = () => {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(category || 'all');

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const url = category 
          ? `${API}/products?category=${category}` 
          : `${API}/products`;
        const response = await axios.get(url);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
    setActiveTab(category || 'all');
  }, [category]);

  const getCategoryTitle = (cat) => {
    const titles = {
      print: 'Print Advertising',
      digital: 'Website & Digital Ads',
      social: 'Social Media',
      advertorial: 'Advertorials',
      events: 'Events',
      all: 'All Advertising Options'
    };
    return titles[cat] || 'All Products';
  };

  const getCategoryDescription = (cat) => {
    const descriptions = {
      print: 'Put your business in the hands of thousands with our biweekly printed edition. We distribute 12,000 to 16,000 copies every two weeks across Los Cabos, La Paz, and Todos Santos.',
      digital: 'Premium digital advertising placements on our website. Fully responsive, high visibility, and great for driving traffic and brand awareness.',
      social: 'Extend your reach with our engaged social media audience of over 19,000 followers on Facebook and Instagram.',
      advertorial: 'Promote your business with a full-page article-style advertisement. Tell your story your way across print, web, and social media.',
      events: 'Give your event the attention it deserves with featured placement across our print, digital, and email channels.',
      all: 'Browse our complete range of advertising options designed to maximize your reach in Baja California Sur.'
    };
    return descriptions[cat] || descriptions.all;
  };

  const filterProducts = (cat) => {
    if (cat === 'all') return products;
    return products.filter(p => p.category === cat);
  };

  const displayProducts = category ? products : filterProducts(activeTab);

  return (
    <div className="min-h-screen bg-white" data-testid="products-page">
      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b">
        <div className="container mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-slate-500 hover:text-navy-700">Home</Link>
            <ChevronRight className="h-4 w-4 text-slate-400" />
            <Link to="/products" className="text-slate-500 hover:text-navy-700">Products</Link>
            {category && (
              <>
                <ChevronRight className="h-4 w-4 text-slate-400" />
                <span className="text-slate-900 font-medium">{getCategoryTitle(category)}</span>
              </>
            )}
          </nav>
        </div>
      </div>

      {/* Header */}
      <section className="bg-slate-50 py-12 md:py-16">
        <div className="container mx-auto px-4">
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" data-testid="products-title">
            {getCategoryTitle(category || activeTab)}
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl font-body">
            {getCategoryDescription(category || activeTab)}
          </p>
        </div>
      </section>

      {/* Products */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          {/* Category tabs (only show if no category in URL) */}
          {!category && (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
              <TabsList className="bg-slate-100 p-1">
                <TabsTrigger 
                  value="all" 
                  className="data-[state=active]:bg-navy-900 data-[state=active]:text-white"
                  data-testid="tab-all"
                >
                  All
                </TabsTrigger>
                <TabsTrigger 
                  value="print"
                  className="data-[state=active]:bg-navy-900 data-[state=active]:text-white"
                  data-testid="tab-print"
                >
                  Print
                </TabsTrigger>
                <TabsTrigger 
                  value="digital"
                  className="data-[state=active]:bg-navy-900 data-[state=active]:text-white"
                  data-testid="tab-digital"
                >
                  Digital
                </TabsTrigger>
                <TabsTrigger 
                  value="social"
                  className="data-[state=active]:bg-navy-900 data-[state=active]:text-white"
                  data-testid="tab-social"
                >
                  Social
                </TabsTrigger>
                <TabsTrigger 
                  value="advertorial"
                  className="data-[state=active]:bg-navy-900 data-[state=active]:text-white"
                  data-testid="tab-advertorial"
                >
                  Advertorial
                </TabsTrigger>
                <TabsTrigger 
                  value="events"
                  className="data-[state=active]:bg-navy-900 data-[state=active]:text-white"
                  data-testid="tab-events"
                >
                  Events
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="spinner" />
            </div>
          ) : displayProducts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">No products found in this category.</p>
              <Link to="/products">
                <Button className="mt-4 bg-navy-900 hover:bg-navy-800">
                  View All Products
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="products-grid">
              {displayProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
